import React, { useState, useEffect, useRef, useCallback } from "react";
import { Card, Container, Row, Button, UncontrolledTooltip, Spinner } from "reactstrap";
import BootstrapTable from "react-bootstrap-table-next";
import Pagination from "../components/Pagination";
import ToolkitProvider from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SimpleHeader from "components/Headers/SimpleHeader";
import DeleteModal from "../../../modals/DeleteModal";
import userServices from "services/userServices";
import helpers from "utils/helpers";
import { useTranslation } from "context/TranslationProvider";
import { useNavigate } from "react-router-dom";
import { useNotification } from "providers/NotificationProvider";
import { useAuth } from "context/AuthContext";
import CloseIcon from "@mui/icons-material/Close";

const RolesTable = () => {
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [isItemsLoading, setIsItemsLoading] = useState(true);
  const { notify } = useNotification();
  const [openModal, setOpenModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);

  const componentRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [searchText, setSearchText] = useState("");
  const { permission } = useAuth();
  const rolePermission = permission?.permissions?.modules?.rolePage;

  const fetchRoles = useCallback(async () => {
    try {
      setIsItemsLoading(true);
      const res = await userServices.getAllRoles();
      const rolesList = res?.data?.roles || (Array.isArray(res?.data) ? res?.data : res?.roles) || [];
      setRoles(rolesList);
    } catch (error) {
      console.error("Error fetching roles:", error);
      notify("error", t("page.roles.fetchFailed", "Failed to fetch role details"));
    } finally {
      setIsItemsLoading(false);
    }
  }, [notify, t]);

  useEffect(() => {
    fetchRoles();
    // eslint-disable-next-line
  }, []);

  const filteredRoles = (roles || []).filter((role) => {
    const searchString = searchText.toLowerCase();
    const name = t(`table.row.${role.slug}`).toLowerCase();
    const slug = (role?.slug || "").toLowerCase();
    const originalName = (role?.name || "").toLowerCase();

    return (
      name.includes(searchString) ||
      slug.includes(searchString) ||
      originalName.includes(searchString)
    );
  });

  const paginatedRoles = filteredRoles.slice(
    currentPage * rowsPerPage,
    (currentPage + 1) * rowsPerPage
  );

  const goToRolePage = (role) => navigate(`/admin/role/${role._id}`);
  const goToAddRole = () => navigate("/admin/role/new");

  const handleModalOpen = (role) => {
    setSelectedRole(role);
    setOpenModal(true);
  };

  const handleModalClose = () => {
    setSelectedRole(null);
    setOpenModal(false);
  };

  const deleteRole = async () => {
    if (!selectedRole) return;

    try {
      const res = await userServices.deleteRole(selectedRole._id);
      if (res?.success) {
        notify("success", t(res?.messageKey || "page.roles.deleteSuccess", res?.message || t("page.roles.deleteSuccess", "Role deleted successfully")));
        fetchRoles();
        handleModalClose();
      } else {
        notify("error", t(res?.messageKey || res?.error || "page.roles.deleteFailed", res?.message || t("page.roles.deleteFailed", "Failed to delete role")));
      }
    } catch (error) {
      console.error("Delete role error:", error);
      notify("error", t("page.roles.deleteFailed", "Failed to delete role"));
    }
  };

  const toCamelCase = (str) => {
    return str
      .replace(/[^a-zA-Z0-9 ]/g, "")
      .split(" ")
      .map((word, index) =>
        index === 0
          ? word.toLowerCase()
          : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      )
      .join("");
  };

  const columns = [
    {
      dataField: "name",
      text: t("table.headers.role", "Role"),
      headerAlign: "left",
      align: "left",
      sort: true,
      formatter: (_, row) => t(`table.row.${row.slug}`),
    },
    {
      dataField: "createdAt",
      text: t("table.headers.date", "Journey Date"),
      headerAlign: "left",
      align: "left",
      sort: true,
      formatter: (cell) => helpers.getFormattedDate(cell, t),
    },
    {
      dataField: "allowedRoutes",
      text: t("table.headers.allowedRoutes", "Allowed Routes"),
      headerAlign: "left",
      align: "left",
      formatter: (cell, row) => {
        const allowed = Array.isArray(cell) ? cell : [];
        const extra = Array.isArray(row.extraRoutes) ? row.extraRoutes : [];
        const filteredRoutes = allowed.filter(route => !extra.includes(route));
        if (!filteredRoutes || filteredRoutes.length === 0 || extra.length === 0) return "N/A";

        const chunkSize = 3;
        const lines = [];
        for (let i = 0; i < filteredRoutes.length; i += chunkSize) {
          lines.push(
            filteredRoutes
              .slice(i, i + chunkSize)
              .map(routeName => t(`sideNav.${toCamelCase(routeName)}`))
              .join(", ")
          );
        }

        return (
          <div className="d-flex flex-column">
            {lines.map((line, index) => (
              <div key={index}>{line}</div>
            ))}
          </div>
        );
      },
    },
    {
      dataField: "actions",
      text: t("table.headers.actions", "Actions"),
      headerAlign: "left",
      align: "left",
      formatter: (_, row) => {
        const canUpdate = rolePermission?.update;
        const canDelete = rolePermission?.delete;

        if (!canUpdate && !canDelete) {
          return <div className="text-center text-muted w-100">N/A</div>;
        }

        return (
          <div className="d-flex align-items-center gap-1">
            <Button
              id={`edit-${row._id}`}
              size="sm"
              onClick={() => canUpdate && goToRolePage(row)}
              disabled={!canUpdate}
            >
              <EditIcon fontSize="small" color={"info"} />
            </Button>
            <UncontrolledTooltip target={`edit-${row._id}`}>
              {!canUpdate ? t("tooltip.editNotAllowed", "You don't have permission to edit") : t("page.roles.actions.edit", "Edit")}
            </UncontrolledTooltip>

            <Button
              id={`delete-${row._id}`}
              size="sm"
              onClick={() => canDelete && handleModalOpen(row)}
              disabled={!canDelete}
            >
              <DeleteIcon fontSize="small" color={"error"} />
            </Button>
            <UncontrolledTooltip target={`delete-${row._id}`}>
              {!canDelete ? t("tooltip.deleteNotAllowed", "Delete not allowed") : t("page.roles.actions.delete", "Delete")}
            </UncontrolledTooltip>
          </div>
        );
      }
    },
  ];

  return (
    <div className="card-header-full-height">
      <SimpleHeader name={t("page.roles.title", "Roles")} parentName={t("sideNav.roles", "Admin")} />
      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <Card className="shadow">
              {isItemsLoading ? (
                <div className="center-loader">
                  <div className="center-loader-content">
                    <Spinner/>
                    <p className="mt-3 text-muted">
                      {t("common.loading", "Loading....")}
                    </p>
                  </div>
                </div>
              ) : (
                <ToolkitProvider key={language} keyField="_id" data={roles} columns={columns} search>
                  {(props) => (
                    <div className="py-4 w-full flex flex-col" ref={componentRef}>
                      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
                        
                        <div className="dataTables_filter px-4 pb-3 mb-md-0 d-flex align-items-center">
                          <label className="d-flex align-items-center mb-0">
                            <span className="text-sm font-medium text-slate-600 mr-2">
                              {t("table.search", "Search")}:
                            </span>

                            <div className="position-relative d-flex align-items-center" style={{ width: "100%", maxWidth: "220px" }}>
                              <input
                                className="form-control form-control-sm"
                                placeholder={t("table.search", "Search")}
                                value={searchText}
                                onChange={(e) => {
                                  setSearchText(e.target.value);
                                  setCurrentPage(0);
                                }}
                                style={{ paddingRight: "24px" }}
                              />
                              {searchText && (
                                <span
                                  onClick={() => {
                                    setSearchText("");
                                    setCurrentPage(0);
                                  }}
                                  className="position-absolute cursor-pointer search-clear-btn"
                                >
                                  <CloseIcon style={{ fontSize: "14px" }} />
                                </span>
                              )}
                            </div>
                          </label>
                        </div>
                        <div className="mr-4 mt--3">
                          {rolePermission?.create && (
                            <Button
                              color="primary"
                              size="sm"
                              onClick={goToAddRole}
                              className="p-2 ml-4 my-2"
                            >
                              {t("page.roles.addRole", "Add Role")}
                            </Button>
                          )}
                        </div>
                      </div>
                      <>
                        <div className="table-responsive max-h-[60vh] overflow-y-auto">
                           <BootstrapTable
                            {...props.baseProps}
                            data={paginatedRoles}
                            bootstrap4
                            bordered={false}
                            noDataIndication={() => (
                              <div className="text-center text-slate-500 py-4">{t("page.roles.noRoles", "No roles found")}</div>
                            )}
                          />
                        </div>
                        <Pagination
                          totalRows={filteredRoles.length}
                          rowsPerPage={rowsPerPage}
                          currentPage={currentPage}
                          onPageChange={(page) => setCurrentPage(page)}
                          onRowsPerPageChange={(size) => {
                            setRowsPerPage(size);
                            setCurrentPage(0);
                          }}
                          t={t}
                        />
                      </>
                    </div>
                  )}
                </ToolkitProvider>
              )}
            </Card>
          </div>
        </Row>
      </Container>

      {/* DELETE MODAL */}
      <DeleteModal
        isOpen={openModal}
        toggle={handleModalClose}
        itemName="role"
        onDelete={deleteRole}
      />
    </div>
  );
};

export default RolesTable;
